import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';

const TermsOfServicePage = () => {
  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing or using the Gleam Guru Cleaning website, mobile applications, or services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.

These Terms constitute a legally binding agreement between you ("Client," "you," or "your") and Gleam Guru Cleaning ("Company," "we," "us," or "our").`
    },
    {
      title: "2. Description of Services",
      content: `Gleam Guru Cleaning provides residential cleaning services in Portland, Oregon and surrounding areas. Our Services include:

• Signature Deep Reset (initial deep reset for new clients)
• Weekly and Biweekly Maintenance cleaning
• Airbnb/Vacation Rental Turnover cleaning
• Add-on services (oven cleaning, refrigerator cleaning, etc.)

Services are performed by independent contractors who have been vetted, background-checked, and trained to meet Gleam Guru standards.`
    },
    {
      title: "3. Account Registration",
      content: `To book services, you must create an account and provide accurate, complete information including:

• Full name
• Email address
• Phone number
• Service address(es)
• Valid payment method

You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.`
    },
    {
      title: "4. Booking and Scheduling",
      content: `• All new clients must book a "Signature Deep Reset" as their first service.
• Recurring maintenance services are available only after completion of the initial Deep Reset.
• Appointments are subject to availability.
• You will receive confirmation of your booking via email.
• You may manage your appointments through the client portal.`
    },
    {
      title: "5. Payment Terms",
      content: `• A valid payment method must be saved on file to complete any booking.
• Payment is processed only after service completion and admin confirmation.
• We accept major credit and debit cards through our secure payment processor (Stripe).
• You authorize automatic charges for completed services when you save a card on file.
• Receipts are sent via email after each charge.
• All prices are in US Dollars (USD).`
    },
    {
      title: "6. Cancellation and Rescheduling",
      content: `• Cancellations 48+ hours before appointment: No charge
• Cancellations 24-48 hours before: 50% service fee
• Cancellations less than 24 hours before: 100% service fee
• Rescheduling is free with 48+ hours notice
• Rescheduling within 48 hours may incur a $25 fee

Please refer to our Policies page for complete details.`
    },
    {
      title: "7. Service Access",
      content: `You agree to:

• Provide accurate access instructions for your property
• Ensure a safe, accessible environment for our cleaners
• Secure pets during the cleaning service
• Remove or secure valuables and fragile items

If access cannot be gained due to incorrect instructions, locked entries, or unresponsive clients, a no-show fee will apply.`
    },
    {
      title: "8. Satisfaction Guarantee",
      content: `We stand behind our work. If you are not satisfied with your cleaning:

• Contact us within 24 hours of service completion
• We will schedule a complimentary re-clean within 3 business days
• If issues persist, partial or full refunds may be issued at our discretion

Claims made after 24 hours or without prior communication may not be honored.`
    },
    {
      title: "9. Liability and Insurance",
      content: `• Gleam Guru Cleaning carries general liability insurance.
• We are not liable for pre-existing damage or normal wear and tear.
• Claims for damage must be reported within 24 hours with photo documentation.
• Maximum liability is limited to the cost of the service or repair of damaged items, whichever is less.
• We are not responsible for damage to items not properly secured or disclosed.`
    },
    {
      title: "10. Independent Contractors",
      content: `Cleaners providing services through Gleam Guru are independent contractors, not employees. As such:

• They set their own schedules within our platform
• They provide their own supplies unless otherwise arranged
• They are responsible for their own taxes and insurance
• They have agreed to Gleam Guru's quality standards and code of conduct`
    },
    {
      title: "11. Privacy",
      content: `Your use of our Services is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using our Services, you consent to our data practices as described in the Privacy Policy.`
    },
    {
      title: "12. Intellectual Property",
      content: `All content on our website and applications, including text, graphics, logos, images, and software, is the property of Gleam Guru Cleaning and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.`
    },
    {
      title: "13. Prohibited Conduct",
      content: `You agree not to:

• Use our Services for any unlawful purpose
• Harass, threaten, or discriminate against our staff or contractors
• Provide false or misleading information
• Attempt to circumvent our booking or payment systems
• Solicit our contractors for services outside our platform`
    },
    {
      title: "14. Termination",
      content: `We reserve the right to suspend or terminate your account and access to our Services at any time, with or without cause, including for:

• Violation of these Terms
• Non-payment or payment disputes
• Abusive behavior toward staff or contractors
• Fraudulent activity

You may close your account at any time by contacting us.`
    },
    {
      title: "15. Dispute Resolution",
      content: `Any disputes arising from these Terms or our Services shall be resolved through:

1. Good-faith negotiation between the parties
2. Mediation in Portland, Oregon
3. Binding arbitration in accordance with Oregon law

You waive any right to participate in class action lawsuits against Gleam Guru Cleaning.`
    },
    {
      title: "16. Modifications to Terms",
      content: `We may update these Terms from time to time. Changes will be posted on this page with an updated "Last Modified" date. Continued use of our Services after changes constitutes acceptance of the modified Terms.`
    },
    {
      title: "17. Governing Law",
      content: `These Terms are governed by the laws of the State of Oregon, without regard to conflict of law principles. Any legal proceedings shall be conducted in Multnomah County, Oregon.`
    },
    {
      title: "18. Contact Information",
      content: `For questions about these Terms of Service, please contact us:

Gleam Guru Cleaning
Serving Portland Metro Area

Email: ContactUs@GleamGurus.com
Phone: (971) 715-0335`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6 drop-shadow-lg">
              Terms of Service
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Please read these terms carefully before using our services.
            </p>
          </motion.div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          {/* Effective Date */}
          <div className="text-center mb-12">
            <p className="text-zinc-600">Effective Date: February 1, 2026</p>
          </div>

          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
            >
              <Card className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  {/* Orange accent line */}
                  <div className="h-1 bg-gradient-to-r from-[#F56F05] to-[#C2410C]" />
                  
                  <div className="p-6 md:p-8">
                    <h2 className="text-xl md:text-2xl font-bold text-zinc-900 font-heading mb-4">
                      {section.title}
                    </h2>
                    <div className="text-zinc-600 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Last Updated */}
          <p className="text-center text-sm text-zinc-500 pt-8">
            Last updated: February 2026
          </p>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
